// src/pages/MatchSetup/UnassignedList.js
//
// Two-line player row:
//   [Name] [Caps if any]                                           [→ A button]
//   Role · Tier N · Bat T# Bowl T# · F# · Conditions · Captain
//
// Sentence case throughout. Strong colors only for the action button
// (Gold A, Navy B) and for cap icons. Caption text is muted grey except
// floater (amber), conditions (red), and overall tier (navy).
//
// Sort toggle: "By rank" (default) vs "A–Z". Rank uses overall_tier
// ascending then total_pts descending so Tier 1 leaders come first.

import React, { useState, useMemo } from 'react';
import { CapBadge, makeCapBadgeMap } from './Caps';

// ─── Help/Legend modal ──────────────────────────────────────
function LegendModal({ onClose }) {
  return (
    <div className="ms-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ms-modal" style={{ maxWidth: 460 }}>
        <div className="ms-modal-head">
          <h3 style={{ color: 'var(--ms-navy)' }}>What do these labels mean?</h3>
          <button className="ms-icon-btn-sm" onClick={onClose}>×</button>
        </div>
        <div className="ms-modal-body" style={{ fontSize: 13, lineHeight: 1.55 }}>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>Role</h4>
          <p style={{ margin: '0 0 14px', color: 'var(--ms-gray-600)' }}>
            <b>Batter</b> — 70%+ of season points from batting.<br/>
            <b>Bowler</b> — 70%+ from bowling.<br/>
            <b>All-rounder</b> — neither dominates.<br/>
            <b>Wicket-keeper</b> — kept in 25%+ of matches played this season (min 3 innings).
          </p>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>
            Player ranks
          </h4>
          <p style={{ margin: '0 0 14px', color: 'var(--ms-gray-600)' }}>
            Each player's rank within today's playing pool. <b>#1 is the best</b>; top 3 highlighted in gold.<br/>
            <b>Bat&nbsp;#N</b> — rank by total runs this season<br/>
            <b>Bowl&nbsp;#N</b> — rank by total wickets this season<br/>
            <b>AR&nbsp;#N</b> — rank by total UCC points (genuine all-rounders only)<br/>
            <b>Keep&nbsp;#N</b> — rank by stumpings + caught-behind<br/>
            <b>Fld&nbsp;#N</b> — rank by catches + run-outs (excludes keeper catches)<br/>
            <i>A pill only appears when the player has done that thing this season.</i>
          </p>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>
            <span style={{ color: 'var(--ms-amber)', fontWeight: 700 }}>F1 / F2 / F3 …</span>
          </h4>
          <p style={{ margin: '0 0 14px', color: 'var(--ms-gray-600)' }}>
            Floater priority. <b>F1</b> = never floated this season (highest priority). <b>F2, F3, …</b> = decreasing priority based on weeks since last floated.
          </p>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>Squad composition (sticky bar)</h4>
          <p style={{ margin: '0 0 14px', color: 'var(--ms-gray-600)' }}>
            Each row counts squad players for that purpose, ranked among today's playing pool. <b>#1 is the best</b>, top 3 highlighted in gold.<br/>
            <b>Batter</b> — every player who's batted (rank by total runs)<br/>
            <b>Bowler</b> — every wicket-taker (rank by total wickets)<br/>
            <b>All-rounder</b> — genuine ARs (rank by total UCC points)<br/>
            <b>Keeper</b> — season + today's keepers (rank by stumpings + caught-behind)<br/>
            <b>Fielder</b> — entire squad (rank by catches + run-outs, excl. keeper catches)<br/>
            <i>A player can appear in multiple rows — that's a real all-rounder.</i>
          </p>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>Season-leader caps</h4>
          <p style={{ margin: '0 0 14px', color: 'var(--ms-gray-600)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CapBadge kind="orange" /> <b>Orange Cap</b> — most runs this season
            </span><br/>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CapBadge kind="purple" /> <b>Purple Cap</b> — most wickets this season
            </span><br/>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CapBadge kind="green" /> <b>Green Cap</b> — best wicket-keeper this season
            </span><br/>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CapBadge kind="blue" /> <b>Blue Cap</b> — best outfield fielder this season
            </span>
          </p>

          <h4 style={{ margin: '0 0 6px', color: 'var(--ms-navy)', fontSize: 13 }}>
            <span style={{ color: 'var(--ms-red)', fontWeight: 700 }}>Conditions</span>
          </h4>
          <p style={{ margin: 0, color: 'var(--ms-gray-600)' }}>
            <b>No bowling</b> / <b>No keeping</b> — declared unavailable for that role today.<br/>
            <b>Leaving 12:00</b> — must leave at this time.<br/>
            Tap a player's name to edit (admin only).
          </p>

        </div>
      </div>
    </div>
  );
}

// ─── Role / tier helpers ────────────────────────────────────
// Roles are STACKABLE. deriveRole() only returns the bat/bowl/AR
// classification. Keeper status is independent — a player can be
// "Batter · Keeper", "All-rounder · Keeper", "Bowler · Keeper", or
// just "Keeper" if they have no batting/bowling history.
//
// Keeper criterion: kept in 25%+ of attended matches this season,
// with a 3-innings floor to filter early-season noise.
function deriveRole(strength) {
  if (!strength) return 'BAT';
  const bat  = Number(strength.batting_pts) || 0;
  const bowl = Number(strength.bowling_pts) || 0;
  if (bat <= 0 && bowl <= 0) return 'BAT';
  const total = bat + bowl;
  const batShare  = total ? bat  / total : 1;
  const bowlShare = total ? bowl / total : 0;
  if (batShare >= 0.7) return 'BAT';
  if (bowlShare >= 0.7) return 'BOWL';
  return 'AR';
}

function isKeeperBySeason(strength) {
  if (!strength) return false;
  const kInn = Number(strength.keeper_innings) || 0;
  if (kInn < 3) return false;
  const att = Math.max(
    Number(strength.matches_attended) || 0,
    Number(strength.batting_innings)  || 0,
    Number(strength.bowling_innings)  || 0,
    kInn,
  );
  return att > 0 && (kInn / att) >= 0.25;
}

function roleLabel(role) {
  if (role === 'BOWL') return 'Bowler';
  if (role === 'AR')   return 'All-rounder';
  return 'Batter';
}

// Build the caption pieces — array of {key, text, className?}
// Caller renders with · separators
function buildCaptionParts({ m, role, strength, priority, isCaptain }) {
  const parts = [];
  const isKeeper = isKeeperBySeason(strength);

  // Role(s) — stacked. Primary BAT/BOWL/AR first, then "Keeper" if applicable.
  // A pure keeper with no batting/bowling history shows just "Keeper".
  const hasBatBowlHistory =
    (Number(strength?.batting_innings) || 0) > 0 ||
    (Number(strength?.bowling_innings) || 0) > 0;
  if (hasBatBowlHistory) {
    parts.push({ key: 'role', text: roleLabel(role) });
    if (isKeeper) {
      parts.push({ key: 'role-wk', text: 'Keeper', className: 'ms-cap-keeper' });
    }
  } else if (isKeeper) {
    parts.push({ key: 'role', text: 'Keeper', className: 'ms-cap-keeper' });
  } else {
    parts.push({ key: 'role', text: roleLabel(role) });
  }

  // Floater priority — amber, uppercase F#  (stays on the role line, NOT in
  // the rank strip — it's a different concept: priority order, not a rank).
  if (priority) {
    parts.push({
      key: 'floater',
      text: priority.label.toUpperCase(),
      className: 'ms-cap-floater',
      title: priority.tier === 1
        ? 'Floater priority F1 — never floated this season'
        : `Floater priority — ${priority.elapsed_weeks} week${priority.elapsed_weeks === 1 ? '' : 's'} since last float`,
    });
  }

  // Guest
  if (m.ucc_id && m.ucc_id.startsWith('GST')) {
    parts.push({ key: 'guest', text: 'Guest', className: 'ms-cap-guest' });
  }

  // Captain
  if (isCaptain) {
    parts.push({ key: 'captain', text: 'Captain', className: 'ms-cap-captain' });
  }

  // Conditions — red
  if (m.no_bowling) {
    parts.push({ key: 'no_bowl', text: 'No bowling', className: 'ms-cap-warn' });
  }
  if (m.no_keeping) {
    parts.push({ key: 'no_keep', text: 'No keeping', className: 'ms-cap-warn' });
  }
  if (m.leaving_early) {
    const t = m.leave_time ? String(m.leave_time).slice(0, 5) : 'early';
    parts.push({ key: 'leave', text: `Leaving ${t}`, className: 'ms-cap-warn' });
  }

  // Per-discipline ranks — built as a SEPARATE list so the renderer can put
  // them on a second line below the role line, in fixed order:
  // Bat → Bowl → AR → Keep → Fld. Empty entries skipped; if the whole list
  // is empty (new guest, no season data) the second line is omitted entirely.
  const rankPills = [];
  if (strength) {
    const order = [
      { key: 'rank-bat',  rank: strength.batting_rank,  prefix: 'Bat',  title: 'Batter rank by total runs this season (today\'s playing pool)' },
      { key: 'rank-bowl', rank: strength.bowling_rank,  prefix: 'Bowl', title: 'Bowler rank by total wickets this season' },
      { key: 'rank-ar',   rank: strength.ar_rank,       prefix: 'AR',   title: 'All-rounder rank by total UCC points this season' },
      { key: 'rank-keep', rank: strength.keeper_rank,   prefix: 'Keep', title: 'Keeper rank by stumpings + caught-behind this season' },
      { key: 'rank-fld',  rank: strength.fielding_rank, prefix: 'Fld',  title: 'Outfield-fielder rank by catches + run-outs this season' },
    ];
    for (const p of order) {
      if (!p.rank) continue;
      rankPills.push({
        key: p.key,
        text: `${p.prefix} #${p.rank}`,
        isTop3: p.rank <= 3,
        title: p.title,
      });
    }
  }

  return { parts, rankPills };
}

export function CaptionLine({ parts }) {
  if (!parts || parts.length === 0) return null;
  const out = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(<span key={`s${i}`} className="ms-cap-sep">·</span>);
    out.push(
      <span key={p.key} className={p.className || ''} title={p.title || ''}>
        {p.text}
      </span>
    );
  });
  return <div className="ms-caption">{out}</div>;
}

export function RankLine({ pills }) {
  if (!pills || pills.length === 0) return null;
  return (
    <div className="ms-rank-line">
      {pills.map(p => (
        <span
          key={p.key}
          className={p.isTop3 ? 'ms-rank-pill ms-rank-pill-top' : 'ms-rank-pill'}
          title={p.title}
        >
          {p.text}
        </span>
      ))}
    </div>
  );
}

// ─── Default export ────────────────────────────────────────
export default function UnassignedList({
  members,
  strengthsByMember,
  turn,           // 'A' | 'B' | null
  canEdit,
  myCaptainSide,
  isAdmin,
  onPick,
  onEditConditions,
  floaterPriorities, // {member_id: priority}
  capHolders,        // { orange_cap_member_id, purple_cap_member_id, green_cap_member_id, blue_cap_member_id }
}) {
  const [showLegend, setShowLegend] = useState(false);
  const [sortMode, setSortMode] = useState('rank'); // 'rank' | 'alpha'

  const capMap = useMemo(() => makeCapBadgeMap(capHolders), [capHolders]);

  // Sort: by rank uses overall_tier asc → total_pts desc → name; alpha is name only.
  // Members with no strength data sink to the bottom in rank mode.
  const sorted = useMemo(() => {
    if (!members) return [];
    const arr = [...members];
    if (sortMode === 'alpha') {
      arr.sort((a, b) => a.display_name.localeCompare(b.display_name));
    } else {
      arr.sort((a, b) => {
        const sa = strengthsByMember.get(a.id);
        const sb = strengthsByMember.get(b.id);
        const ta = sa?.overall_tier ?? 99;
        const tb = sb?.overall_tier ?? 99;
        if (ta !== tb) return ta - tb;                      // T1 first
        const pa = Number(sa?.total_pts) || 0;
        const pb = Number(sb?.total_pts) || 0;
        if (pb !== pa) return pb - pa;                      // higher pts first
        return a.display_name.localeCompare(b.display_name);
      });
    }
    return arr;
  }, [members, sortMode, strengthsByMember]);

  if (!members || members.length === 0) {
    return (
      <div style={{
        gridColumn: '1/-1', textAlign: 'center', padding: 18,
        color: 'var(--ms-gray-500)', fontStyle: 'italic', fontSize: 13
      }}>
        All present members assigned
      </div>
    );
  }

  function buttonFor(m) {
    if (!canEdit || !turn) {
      return <button className="ms-pick-btn ms-side-a" disabled>—</button>;
    }
    if (isAdmin || myCaptainSide === turn) {
      const cls = turn === 'A' ? 'ms-pick-btn ms-side-a' : 'ms-pick-btn ms-side-b';
      return (
        <button className={cls} onClick={() => onPick(m.id, turn)}>
          → {turn}
        </button>
      );
    }
    return <button className="ms-pick-btn ms-side-a" disabled>—</button>;
  }

  return (
    <>
      {showLegend && <LegendModal onClose={() => setShowLegend(false)} />}
      <div className="ms-unassigned-info-row">
        <div className="ms-sort-toggle" role="group" aria-label="Sort unassigned list">
          <button
            type="button"
            className={`ms-sort-opt ${sortMode === 'rank' ? 'ms-active' : ''}`}
            onClick={() => setSortMode('rank')}
            title="Sort by overall tier (T1 first), then points"
          >
            By rank
          </button>
          <button
            type="button"
            className={`ms-sort-opt ${sortMode === 'alpha' ? 'ms-active' : ''}`}
            onClick={() => setSortMode('alpha')}
            title="Sort alphabetically"
          >
            A–Z
          </button>
        </div>
        <button
          type="button"
          className="ms-help-btn"
          onClick={() => setShowLegend(true)}
          title="Tap to see what each label means"
          aria-label="Help — what do these labels mean?"
        >
          ⓘ&nbsp;Guide
        </button>
      </div>
      <div className="ms-unassigned-grid">
        {sorted.map(m => {
          const strength = strengthsByMember.get(m.id);
          const role = deriveRole(strength);
          const priority = floaterPriorities && floaterPriorities[m.id];
          const caps = capMap.get(m.id) || [];
          const { parts, rankPills } = buildCaptionParts({ m, role, strength, priority, isCaptain: false });
          return (
            <div className="ms-una-row" key={m.id}>
              <div
                className="ms-una-name"
                onClick={() => onEditConditions && onEditConditions(m)}
                title={onEditConditions ? 'Tap to edit conditions' : ''}
              >
                <div className="ms-name-row">
                  <span className="ms-name-text">{m.display_name}</span>
                  {caps.map(cap => <CapBadge key={cap} kind={cap} />)}
                </div>
                <CaptionLine parts={parts} />
                <RankLine pills={rankPills} />
              </div>
              {buttonFor(m)}
            </div>
          );
        })}
      </div>
    </>
  );
}
